/**
 * Measured keyframe (I-frame) spacing from `GET camera/{id}/status/`
 * (ENG-2222, actuate_admin #3066).
 *
 * Live view is publish-on-demand LL-HLS: the first playlist cannot be served
 * until a complete segment closes on a keyframe, so time-to-video is about one
 * to two keyframe gaps, and the rewind window scales with the gap too. A
 * camera sending a keyframe every 12 s takes ~16 s to start live view; one at
 * 1–2 s starts in a few seconds.
 *
 * All three fields are null until a connector running the measuring puller
 * reports them, which renders as "—" with no warning.
 *
 * `keyframe_interval_is_lower_bound` (actuate_admin #3070): true when the
 * puller saw no second keyframe within its window, so seconds and frames are
 * minimums ("no keyframe for at least N s"), shown as "> N s". False or
 * null/absent means a measured median (or nothing measured), as before.
 */

/** Above this many seconds between keyframes, live view starts noticeably slowly. */
export const KEYFRAME_INTERVAL_WARN_SECONDS = 4;

export interface KeyframeIntervalFields {
  keyframe_interval_seconds?: number | null;
  keyframe_interval_frames?: number | null;
  keyframe_measured_at?: string | null;
  native_frame_rate?: number | null;
  /** True when seconds/frames are minimums, not a measured median. */
  keyframe_interval_is_lower_bound?: boolean | null;
}

export type KeyframeIntervalLevel = "ok" | "warning" | "unknown";

export interface KeyframeIntervalSummary {
  level: KeyframeIntervalLevel;
  /** "12.0 s (68 frames)", "> 12.0 s (> 68 frames)", or "—" when not measured. */
  display: string;
  seconds: number | null;
  /** The interval is at least `seconds` (no second keyframe seen yet). */
  lowerBound: boolean;
  /** The fix, in words; null unless `level` is "warning". */
  hint: string | null;
  measuredAt: string | null;
}

const finite = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

/** Frames per second to quote in the fix: the stream's native rate, else the measured one. */
const framesPerSecond = (fields: KeyframeIntervalFields): number | null => {
  const native = finite(fields.native_frame_rate);
  if (native != null && native > 0) return native;
  const seconds = finite(fields.keyframe_interval_seconds);
  const frames = finite(fields.keyframe_interval_frames);
  if (seconds != null && seconds > 0 && frames != null && frames > 0) {
    return frames / seconds;
  }
  return null;
};

/** "12" for whole numbers, "12.5" otherwise. */
const trimSeconds = (seconds: number): string =>
  Number.isInteger(seconds) ? String(seconds) : seconds.toFixed(1);

/** Display form of the interval alone, e.g. "12" or "12.5" (for titles). */
export const formatKeyframeSeconds = (seconds: number): string =>
  trimSeconds(Math.round(seconds * 10) / 10);

/**
 * The fix for a long keyframe interval: how long live view will take to start,
 * and the I-frame setting to use instead.
 */
export const keyframeIntervalHint = (
  fields: KeyframeIntervalFields
): string | null => {
  const seconds = finite(fields.keyframe_interval_seconds);
  if (seconds == null) return null;
  const lowerBound = fields.keyframe_interval_is_lower_bound === true;
  const fps = framesPerSecond(fields);
  const frameRange =
    fps != null
      ? ` (${Math.max(1, Math.round(fps))}–${Math.max(
          2,
          Math.round(2 * fps)
        )} frames)`
      : "";
  const startTime = lowerBound
    ? `at least about ${Math.round(seconds)} s`
    : `about ${Math.round(seconds)}–${Math.round(2 * seconds)} s`;
  return (
    (lowerBound
      ? `No keyframe seen for at least ${formatKeyframeSeconds(seconds)} s. `
      : "") +
    `Live view needs a keyframe to start, so this camera will take ` +
    `${startTime} to start live view, ` +
    `and its rewind window will be long. Set the camera's I-frame ` +
    `(keyframe/GOP) interval to 1–2 seconds${frameRange}.`
  );
};

export const summarizeKeyframeInterval = (
  fields: KeyframeIntervalFields | null | undefined
): KeyframeIntervalSummary => {
  const seconds = finite(fields?.keyframe_interval_seconds);
  const measuredAt = fields?.keyframe_measured_at ?? null;
  if (!fields || seconds == null) {
    return {
      level: "unknown",
      display: "—",
      seconds: null,
      lowerBound: false,
      hint: null,
      measuredAt,
    };
  }
  const lowerBound = fields.keyframe_interval_is_lower_bound === true;
  const prefix = lowerBound ? "> " : "";
  const frames = finite(fields.keyframe_interval_frames);
  const display = `${prefix}${seconds.toFixed(1)} s${
    frames != null ? ` (${prefix}${Math.round(frames)} frames)` : ""
  }`;
  // A lower bound over the threshold is already conclusive; one at or under
  // it says nothing yet, so it gets no verdict (not a green tick).
  const warning = seconds > KEYFRAME_INTERVAL_WARN_SECONDS;
  return {
    level: warning ? "warning" : lowerBound ? "unknown" : "ok",
    display,
    seconds,
    lowerBound,
    hint: warning ? keyframeIntervalHint(fields) : null,
    measuredAt,
  };
};

/** "measured 5 min ago"; null when there is no (valid) timestamp. */
export const keyframeMeasuredText = (
  measuredAt: string | null | undefined,
  nowMs = Date.now()
): string | null => {
  if (!measuredAt) return null;
  const then = new Date(measuredAt).getTime();
  if (!Number.isFinite(then)) return null;
  const diffS = Math.max(0, Math.round((nowMs - then) / 1000));
  if (diffS < 60) return "measured just now";
  const diffM = Math.round(diffS / 60);
  if (diffM < 60) return `measured ${diffM} min ago`;
  const diffH = Math.round(diffM / 60);
  if (diffH < 48) return `measured ${diffH} h ago`;
  return `measured ${Math.round(diffH / 24)} d ago`;
};
